import {
  MotifCard,
  MotifCardBody,
  MotifCardHeader,
  MotifChip,
  MotifTooltip,
  MotifTruncate,
  MotifIcon,
} from "@ey-xd/motif-react";
import "./OrderCardExtension.scss";
import { useEffect, useState } from "react";
import { ViewDetails } from "../ViewDetails/ViewDetails";
import {
  calculatePeriodTotalCost,
  calculateTotalCost,
  getDuration,
  getDurationType,
  getRelationDuration,
} from "../../OrderExtensionHelper";
import {
  skuIDInfo,
  unitCostInfo,
  unitPerPeriodInfo,
  numberOfPeriodsInfo,
  subTotalInfo,
  fixedCostInfo,
  variableCostInfo,
} from "../../../../utils/skuTable";
import { IconoirArrowRight } from "@ey-xd/motif-icon";
import { ResponseDetails } from "../../../../store/slices/CheckoutSlice";
import {
  OrderExtensionProps,
  SkuDetailsExistingOrderType,
  ViewDetailType,
} from "../../type";
import { validateCostInterval } from "../../../../utils/Validator";
import InfoTooltip from "../../../../components/InfoTooltip";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { fetchSkuConfigData } from "../../../../store/slices/CTDBSkuConfigSlice";
import Loader from "../../../../components/Loader";
import {
  getCostIntervalForCostMetric,
  getUnitsUsedForPeriod,
} from "../../../Checkout/CheckoutHelper";

interface OrderCardProps {
  productName: string;
  assetAdvice?: null | JSX.Element | undefined | string;
  skuDetails: SkuDetailsExistingOrderType[]; // Assuming skuDetails is of type OrderBundleType
  checkOutListState?: ResponseDetails | null;
  setOrderExtensionDataState: React.Dispatch<
    React.SetStateAction<OrderExtensionProps | null>
  >;
  orderExtensionDataState: OrderExtensionProps | null;
}
export const OrderCardExtension = ({
  productName,
  assetAdvice,
  skuDetails,
  setOrderExtensionDataState,
  orderExtensionDataState,
}: OrderCardProps) => {
  const [skuListViewData, setSkuListViewData] = useState(skuDetails);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
  const [viewDetailsData, setViewDetailsData] = useState<ViewDetailType>({
    isViewDetailsOpen: isViewDetailsOpen,
    skuId: "",
    skuName: "",
    skuDescription: "",
    skuCommercialStatus: "",
    skuCost: 0,
    returnable: false,
    skuCostInterval: "",
    setIsViewDetailsOpen: setIsViewDetailsOpen,
    assetAdvice: assetAdvice || null,
    CostMetricCd: "",
    skuCostMacroCategoryCd: "",
    skuCostMetricNSR: 0,
    skuIndexation: 0,
    skuStatusName: "",
  });
  const dispatch = useDispatch();
  const configData = useSelector(
    (state: RootState) => state.CTDBSkuConfigSlice.data,
  );

  useEffect(() => {
    if (configData === null || configData === undefined) {
      dispatch(fetchSkuConfigData() as any);
    }
  }, [configData]);

  const handleViewDetails = (skuDetails: SkuDetailsExistingOrderType) => {
    setIsViewDetailsOpen(true);
    if (!viewDetailsData || viewDetailsData === null) {
      return;
    }
    setViewDetailsData({
      ...viewDetailsData,
      isViewDetailsOpen: true,
      skuId: skuDetails.SkuId,
      skuName: skuDetails.SkuName,
      skuDescription: skuDetails.SkuDescription || "",
      skuCommercialStatus: skuDetails.OrderSkuStatus,
      skuCost: skuDetails.CostMetricCost,
      returnable: skuDetails.IsReturnable,
      skuCostInterval: skuDetails.CostIntervalCd,
      CostMetricCd: skuDetails.CostMetricCd,
      skuCostMacroCategoryCd: skuDetails.CostMacroCategoryCd || "",
      skuCostMetricNSR: skuDetails.CostMetricNSR,
      skuIndexation: skuDetails.Indexation,
      skuStatusName: skuDetails.OrderSkuStatus ? skuDetails.OrderSkuStatus : "",
    });
  };

  const isFixedCostSku = (sku: any) => {
    return sku.CostMetricCd === "fix_cost" || sku.CostIntervalCd === "onetime";
  };

  useEffect(() => {
    if (skuListViewData && skuListViewData !== null) {
      const isHavingDurationError: boolean[] = [];
      setSkuListViewData(
        skuListViewData.map((sku: any) => {
          const isError = !validateCostInterval(
            sku.CostIntervalCd,
            sku.OrderInterval,
            sku.CostIntervalMin,
          );
          isHavingDurationError.push(isError);
          return {
            ...sku,
            OrderSkuQunatity:
              sku?.CostIntervalCd == "onetime" ? 1 : sku?.OrderSkuQunatity,
            Quantity:
              sku?.CostIntervalCd == "onetime" ? 1 : sku?.OrderSkuQunatity,
            QuantityError:
              sku?.CostIntervalCd == "onetime" ? "" : sku?.QuantityError || "",
            Duration: sku?.CostIntervalCd == "onetime" ? 1 : 0,
            DurationError:
              sku?.CostIntervalCd == "onetime" ? "" : sku?.DurationError || "",
            SkuRelation: sku?.SkuRelation.map((relation: any) => {
              const isError = !validateCostInterval(
                relation.CostIntervalCd,
                relation.OrderInterval,
                relation.CostIntervalMin,
              );
              isHavingDurationError.push(isError);
              return {
                ...relation,
                OrderSkuQunatity:
                  relation.CostIntervalCd == "onetime"
                    ? 1
                    : relation.OrderSkuQunatity,
                Quantity:
                  relation.CostIntervalCd == "onetime"
                    ? 1
                    : relation.OrderSkuQunatity,
                QuantityError:
                  relation.CostIntervalCd == "onetime"
                    ? ""
                    : relation.QuantityError,
                Duration: relation.CostIntervalCd == "onetime" ? 1 : 0,
                DurationError:
                  relation.CostIntervalCd == "onetime"
                    ? ""
                    : relation.DurationError || "",
              };
            }),
          };
        }),
      );
      // check any one SKU has duration error
    }
  }, []);

  const getCostType = (CostMetricCd: string) => {
    if (!configData) return "";
    const costType =
      configData?.CostCategory.find(
        (item) => item.CostMetricCd === CostMetricCd,
      )?.CostMetricType || "";
    return costType || "";
  };

  const getCostTypeLabel = (CostMetricCd: string) => {
    if (!configData) return "";
    const costType = getCostType(CostMetricCd);
    if (costType === "Fixed") {
      return (
        <div className="sku-number-type fixed">
          <MotifTooltip
            contentClassName="tooltip-content"
            placement="top"
            hideCloseButton
            flip
            offset={[0, 8]}
            allowHover
            allowClick={true}
            trigger={
              <MotifChip
                className={`sku-badge fixed`}
                onClick={() => {}}
                disabled={false}
              >
                Fixed
              </MotifChip>
            }
            variant="default"
          >
            <div className="label-info-icon-text">{fixedCostInfo()}</div>
          </MotifTooltip>
        </div>
      );
    } else if (costType === "Variable") {
      return (
        <div className="sku-number-type variable">
          <MotifTooltip
            contentClassName="tooltip-content"
            placement="top"
            hideCloseButton
            flip
            offset={[0, 8]}
            allowHover
            allowClick={true}
            trigger={
              <MotifChip
                className={`sku-badge variable`}
                onClick={() => {}}
                disabled={false}
              >
                Variable
              </MotifChip>
            }
            variant="default"
          >
            <div className="label-info-icon-text">{variableCostInfo()}</div>
          </MotifTooltip>
        </div>
      );
    } else {
      return "";
    }
  };

  if (!configData) {
    return <Loader />;
  }

  return (
    <>
      <div className="order-card-extenson order-card">
        <MotifCard className="motif-card-default">
          <MotifCardHeader>
            <div className="motif-card-subheader">
              <div className="motif-h6-default-regular">{productName}</div>
            </div>
          </MotifCardHeader>
          <MotifCardBody>
            <table className="motif-table-standard">
              <thead className="table-header">
                <tr>
                  <th className="sku-th">
                    <div className="label-with-info">
                      <div>SKU name/SKU ID</div>
                      <div>
                        <InfoTooltip
                          description={skuIDInfo()}
                          id="sku-id-info"
                        />
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="label-with-info right-align-grid">
                      <div>Unit Cost</div>
                      <div>
                        <InfoTooltip
                          description={unitCostInfo()}
                          id="unit-cost-info"
                        />
                      </div>
                    </div>
                  </th>
                  <th className="qty-td">
                    <div className="label-with-info">
                      <div>Units per period</div>
                      <div>
                        <InfoTooltip
                          description={unitPerPeriodInfo()}
                          id="unit-per-period-info"
                        />
                      </div>
                    </div>
                  </th>
                  <th className="duration-td">
                    <div className="label-with-info">
                      <div>Number of periods</div>
                      <div>
                        <InfoTooltip
                          description={numberOfPeriodsInfo()}
                          id="number-of-periods-info"
                        />
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="label-with-info right-align-grid">
                      <div>Subtotal</div>
                      <div>
                        <InfoTooltip
                          description={subTotalInfo()}
                          id="sub-total-info"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {skuListViewData.map(
                  (sku: SkuDetailsExistingOrderType, index: number) => {
                    if (sku?.SkuRelation?.length === 0) {
                      return (
                        <tr key={index}>
                          <td className={`sku-cell`}>
                            <div className="sku-title title">
                              <MotifTruncate
                                maxLines={1}
                                maxWidth={160}
                                tooltipProps={{ placement: "top" }}
                              >
                                {sku?.SkuName}
                              </MotifTruncate>
                            </div>
                            <div className="sku-number suggested-table-content">
                              #
                              <MotifTruncate
                                maxLines={1}
                                maxWidth={100}
                                tooltipProps={{ placement: "top" }}
                              >
                                {sku?.SkuId}
                              </MotifTruncate>
                              {getCostTypeLabel(sku?.CostMetricCd)}
                            </div>
                            <div
                              onClick={() => handleViewDetails(sku)}
                              className="view-details-link"
                            >
                              <div>View Details</div>
                              <div className="icon-grid">
                                {<MotifIcon iconFunction={IconoirArrowRight} />}
                              </div>
                            </div>
                          </td>
                          <td className="unit-cost-td single-sku">
                            <div className="title right-align-field">
                              {sku?.CostMetricCost.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                              })}
                            </div>
                            <div className="suggested-table-content right-align-field-flex">
                              {getCostIntervalForCostMetric(
                                sku.CostIntervalCd,
                                sku.CostMetricCd,
                              )}
                            </div>
                          </td>
                          <td className="qty-td single-sku">
                            <div className="title">
                              {isFixedCostSku(sku) ? (
                                <span>01</span>
                              ) : (
                                <span>
                                  {sku?.OrderSkuQunatity?.toString() ?? ""}
                                </span>
                              )}
                            </div>
                            <div className="suggested-table-content">
                              {getUnitsUsedForPeriod(
                                sku.CostIntervalCd,
                                sku.CostMetricCd,
                                sku?.OrderSkuQunatity,
                              )}
                            </div>
                          </td>
                          <td className="duration-td">
                            <div className="suggested-table-content1">
                              {getDuration(
                                sku,
                                skuListViewData,
                                setSkuListViewData,
                                null,
                                setOrderExtensionDataState,
                                orderExtensionDataState,
                              )}
                            </div>
                            <div className="suggested-table-content">
                              {getDurationType(sku?.CostIntervalCd)}
                            </div>
                          </td>
                          <td className="subtotal-td single-sku">
                            <div>
                              {calculateTotalCost(sku) === 0 ? (
                                <div className="empty-subtotal">--</div>
                              ) : (
                                <>
                                  <div
                                    className={"order-total right-align-field"}
                                  >
                                    {calculateTotalCost(sku)}{" "}
                                  </div>
                                  <div className="suggested-table-content right-align-field-flex">
                                    {calculatePeriodTotalCost(sku)} /
                                    {getDurationType(sku?.CostIntervalCd)
                                      ? getDurationType(sku?.CostIntervalCd)
                                      : ""}
                                  </div>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    } else {
                      return (
                        <tr key={index}>
                          <td className={`sku-cell}`}>
                            <ul className="tree">
                              <li className="tree-trigger">
                                <div className="table-cell">
                                  <div>
                                    <span className="sku-title">
                                      <MotifTruncate
                                        maxLines={1}
                                        maxWidth={160}
                                      >
                                        {sku?.SkuName}
                                      </MotifTruncate>
                                    </span>
                                  </div>
                                  <div className="sku-number suggested-table-content">
                                    #
                                    <MotifTruncate
                                      maxLines={1}
                                      maxWidth={100}
                                      tooltipProps={{ placement: "top" }}
                                    >
                                      {sku?.SkuId}
                                    </MotifTruncate>
                                    {getCostTypeLabel(sku?.CostMetricCd)}
                                  </div>
                                  <div
                                    onClick={() => handleViewDetails(sku)}
                                    className="view-details-link"
                                  >
                                    <div>View Details</div>
                                    <div className="icon-grid">
                                      {
                                        <MotifIcon
                                          iconFunction={IconoirArrowRight}
                                        />
                                      }
                                    </div>
                                  </div>
                                </div>
                              </li>
                              <ul>
                                {sku?.SkuRelation?.map((skuRelation: any) => {
                                  return (
                                    <li>
                                      <div className="table-cell">
                                        <div className="sku-title">
                                          <MotifTruncate
                                            maxLines={1}
                                            maxWidth={160}
                                          >
                                            {skuRelation.SkuName}
                                          </MotifTruncate>
                                        </div>
                                        <div className="sku-number">
                                          <div className="sku-number suggested-table-content">
                                            #
                                            <MotifTruncate
                                              maxLines={1}
                                              maxWidth={80}
                                              tooltipProps={{
                                                placement: "top",
                                              }}
                                            >
                                              {skuRelation.SkuId}
                                            </MotifTruncate>
                                            {getCostTypeLabel(
                                              skuRelation?.CostMetricCd,
                                            )}
                                          </div>
                                          <span
                                            onClick={() =>
                                              handleViewDetails(skuRelation)
                                            }
                                            className="view-details-link"
                                          >
                                            <div>View Details</div>
                                            <div className="icon-grid">
                                              {
                                                <MotifIcon
                                                  iconFunction={
                                                    IconoirArrowRight
                                                  }
                                                />
                                              }
                                            </div>
                                          </span>
                                        </div>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            </ul>
                          </td>
                          <td className="unit-cost-td relation-col">
                            <div className="table-cell-child table-cell unit-cost-relation right-align-field">
                              {sku?.CostMetricCost.toLocaleString("en-US", {
                                style: "currency",
                                currency: "USD",
                                maximumFractionDigits: 2,
                                minimumFractionDigits: 2,
                              })}
                              <div className="suggested-table-content right-align-field-flex">
                                {getCostIntervalForCostMetric(
                                  sku.CostIntervalCd,
                                  sku.CostMetricCd,
                                )}
                              </div>
                            </div>
                            {sku?.SkuRelation?.map((skuRelation: any) => {
                              return (
                                <div
                                  key={skuRelation.SkuId}
                                  className="table-cell-child table-cell unit-cost-relation right-align-field"
                                >
                                  {skuRelation.CostMetricCost.toLocaleString(
                                    "en-US",
                                    {
                                      style: "currency",
                                      currency: "USD",
                                      maximumFractionDigits: 2,
                                      minimumFractionDigits: 2,
                                    },
                                  )}
                                  <div className="suggested-table-content right-align-field-flex">
                                    {getCostIntervalForCostMetric(
                                      skuRelation.CostIntervalCd,
                                      skuRelation.CostMetricCd,
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </td>
                          <td className="qty-td relation-col">
                            <div className="table-cell-child table-cell relation-qty">
                              {isFixedCostSku(sku) ? (
                                <>
                                  <div className="fix-qnt">01 </div>
                                </>
                              ) : (
                                <>{sku?.OrderSkuQunatity?.toString() ?? ""}</>
                              )}
                              <div className="suggested-table-content">
                                {getUnitsUsedForPeriod(
                                  sku.CostIntervalCd,
                                  sku.CostMetricCd,
                                  sku?.OrderSkuQunatity,
                                )}
                              </div>
                            </div>
                            {sku?.SkuRelation?.map((skuRelation: any) => {
                              return (
                                <div
                                  className="table-cell-child table-cell relation-qty quantity-relation fix-quantity-relation"
                                  key={skuRelation.SkuId}
                                >
                                  {isFixedCostSku(skuRelation) ? (
                                    <div className="fix-qnt-child">01</div>
                                  ) : (
                                    <>
                                      <>
                                        {skuRelation?.OrderSkuQunatity?.toString() ??
                                          ""}
                                      </>
                                      <div className="suggested-table-content">
                                        {getUnitsUsedForPeriod(
                                          skuRelation.CostIntervalCd,
                                          skuRelation.CostMetricCd,
                                          skuRelation?.OrderSkuQunatity,
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </td>
                          <td className="duration-td relation-col">
                            <div className="suggested-table-content table-cell-child table-cell relation-qty support-text">
                              {getDuration(
                                sku,
                                skuListViewData,
                                setSkuListViewData,
                                null,
                                setOrderExtensionDataState,
                                orderExtensionDataState,
                              )}
                              <div className="suggested-table-content">
                                {getDurationType(sku?.CostIntervalCd)}
                              </div>
                            </div>

                            {sku?.SkuRelation?.map((skuRelation: any) => {
                              return (
                                <>
                                  <div
                                    className="relation-duration table-cell-child table-cell relation-qty suggested-table-content support-text"
                                    key={skuRelation.SkuId}
                                  >
                                    {getRelationDuration(
                                      skuRelation,
                                      skuListViewData,
                                      setSkuListViewData,
                                      sku.SkuId,
                                      setOrderExtensionDataState,
                                      orderExtensionDataState,
                                    )}
                                    <div className="suggested-table-content">
                                      {getDurationType(
                                        skuRelation?.CostIntervalCd,
                                      )}
                                    </div>
                                  </div>
                                </>
                              );
                            })}
                          </td>
                          <td className="subtotal-td relation-col">
                            <div
                              className={`relation-total table-cell-child table-cell unit-cost-relation`}
                            >
                              <div>
                                {calculateTotalCost(sku) === 0 ? (
                                  "--"
                                ) : (
                                  <>
                                    <div
                                      className={
                                        "order-total right-align-field"
                                      }
                                    >
                                      {calculateTotalCost(sku)}{" "}
                                    </div>
                                    <div className="suggested-table-content right-align-field">
                                      {calculatePeriodTotalCost(sku)} /
                                      {getDurationType(sku?.CostIntervalCd)
                                        ? getDurationType(sku?.CostIntervalCd)
                                        : ""}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            {sku?.SkuRelation?.map((skuRelation: any) => {
                              const total_with_padding =
                                skuRelation.CostIntervalCd == "onetime"
                                  ? "no-padding"
                                  : "total-width-padding";
                              return (
                                <>
                                  <div
                                    className={`relation-total table-cell-child table-cell unit-cost-relation ${total_with_padding}`}
                                    key={skuRelation.SkuId}
                                  >
                                    <div>
                                      {calculateTotalCost(skuRelation) === 0 ? (
                                        "--"
                                      ) : (
                                        <>
                                          <div
                                            className={
                                              "order-total right-align-field"
                                            }
                                          >
                                            {calculateTotalCost(
                                              skuRelation,
                                            )}{" "}
                                          </div>
                                          <div className="suggested-table-content right-align-field">
                                            {calculatePeriodTotalCost(
                                              skuRelation,
                                            )}{" "}
                                            /
                                            {getDurationType(
                                              skuRelation?.CostIntervalCd,
                                            )
                                              ? getDurationType(
                                                  skuRelation?.CostIntervalCd,
                                                )
                                              : ""}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </>
                              );
                            })}
                          </td>
                        </tr>
                      );
                    }
                  },
                )}
              </tbody>
            </table>
          </MotifCardBody>
        </MotifCard>
      </div>
      {isViewDetailsOpen && <ViewDetails viewDetailsData={viewDetailsData} />}
    </>
  );
};
